import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import Card from 'components/card';
import { CONFIG } from 'variables/config';
import { MdArrowBack, MdConstruction, MdContentCopy, MdRefresh, MdSave, MdWarning } from 'react-icons/md';
import DuplicateMappingModal from './DuplicateMappingModal';
import NoMatchLeadsTab from './NoMatchLeadsTab';
import ImportedLeadsTab from './ImportedLeadsTab';
import WorktypeTodoTab from './WorktypeTodoTab';
import BlockRulesTab from './BlockRulesTab';
import BlockedLeadsTab from './BlockedLeadsTab';

// Page Batiweb (HelloArtisan) : revente des leads no-match. Voir notes/integration-helloartisan.md.

interface TypeOption {
    id: number;
    label: string;
}

interface HelloArtisanTypes {
    isowner: TypeOption[];
    housingtype: TypeOption[];
    delay: TypeOption[];
    budget: TypeOption[];
    worktype: TypeOption[];
}

interface MatchingRow {
    industry: string;
    subIndustry: string;
    subIndustryId: number | null;
    worktypeId: number | null;
    worktypeLabel: string | null;
    note: string;
    updatedBy: string | null;
    updatedAt: string | null;
}

// Valeur du select : '' = pas encore paramétré, 'none' = pas d'équivalent, sinon l'id du worktype.
type Draft = { worktype: string; note: string };

const BATIWEB_TABS = [
    { key: 'noMatch', label: 'Leads no-match' },
    { key: 'imported', label: 'En cours' },
    { key: 'blocked', label: 'Bloqués historiques' },
    { key: 'blockRules', label: 'Règles de blocage' },
    { key: 'matching', label: 'Matching produits' },
    { key: 'todo', label: 'Worktypes à matcher' },
    { key: 'types', label: 'Valeurs autorisées (/types)' },
] as const;
type BatiwebTab = typeof BATIWEB_TABS[number]['key'];

const DEFAULT_INDUSTRY = 'Rénovation Energetique';

const TYPE_LISTS: { key: keyof Omit<HelloArtisanTypes, 'worktype'>; label: string }[] = [
    { key: 'isowner', label: 'Statut (isowner)' },
    { key: 'housingtype', label: 'Logement (housingtype)' },
    { key: 'delay', label: 'Délai (delay)' },
    { key: 'budget', label: 'Budget (budget)' },
];

const rowKey = (row: MatchingRow) => `${row.industry}::${row.subIndustry}`;

const draftFromRow = (row: MatchingRow): Draft => ({
    worktype: row.worktypeId != null ? String(row.worktypeId) : row.updatedAt ? 'none' : '',
    note: row.note || '',
});

export default function BackOfficeBatiweb() {
    const navigate = useNavigate();
    // Arrivé depuis le schéma de la page Waterfall : bouton retour.
    const fromWaterfall = (useLocation().state as { from?: string } | null)?.from === 'waterfall';
    const [activeTab, setActiveTab] = useState<BatiwebTab>('noMatch');

    const [types, setTypes] = useState<HelloArtisanTypes | null>(null);
    const [typesError, setTypesError] = useState('');
    const [typesLoading, setTypesLoading] = useState(false);

    const [rows, setRows] = useState<MatchingRow[]>([]);
    const [drafts, setDrafts] = useState<Record<string, Draft>>({});
    const [rowsLoading, setRowsLoading] = useState(true);
    const [industryFilter, setIndustryFilter] = useState(DEFAULT_INDUSTRY);
    const [savingKey, setSavingKey] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [worktypeSearch, setWorktypeSearch] = useState('');
    const [duplicateSource, setDuplicateSource] = useState<MatchingRow | null>(null);

    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    const fetchTypes = useCallback(async (refresh = false) => {
        try {
            setTypesLoading(true);
            setTypesError('');
            const res = await axios.get(`${CONFIG.apiUrl}/admin/batiweb/types`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                params: refresh ? { refresh: '1' } : undefined,
            });
            setTypes(res.data.types);
        } catch (e: any) {
            setTypesError(e.response?.data?.message || 'Impossible de récupérer GET /types.');
        } finally {
            setTypesLoading(false);
        }
    }, []);

    const fetchRows = useCallback(async () => {
        try {
            setRowsLoading(true);
            const res = await axios.get(`${CONFIG.apiUrl}/admin/batiweb/matching`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            const fetched: MatchingRow[] = res.data.rows;
            setRows(fetched);
            setDrafts(Object.fromEntries(fetched.map((row) => [rowKey(row), draftFromRow(row)])));
        } catch (e: any) {
            setError(e.response?.data?.message || 'Impossible de charger le matching.');
        } finally {
            setRowsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTypes();
        fetchRows();
    }, [fetchTypes, fetchRows]);

    const industries = useMemo(() => Array.from(new Set(rows.map((row) => row.industry))), [rows]);

    // Si l'industrie par défaut n'existe pas (renommée), on affiche tout plutôt qu'une liste vide.
    useEffect(() => {
        if (industries.length > 0 && industryFilter && !industries.includes(industryFilter)) {
            setIndustryFilter('');
        }
    }, [industries, industryFilter]);

    const visibleRows = useMemo(
        () => (industryFilter ? rows.filter((row) => row.industry === industryFilter) : rows),
        [rows, industryFilter]
    );

    const sortedWorktypes = useMemo(
        () => (types ? [...types.worktype].sort((a, b) => a.label.localeCompare(b.label, 'fr')) : []),
        [types]
    );
    const worktypeIds = useMemo(() => new Set(types?.worktype.map((w) => w.id) ?? []), [types]);

    const counts = useMemo(() => {
        let mapped = 0;
        let none = 0;
        let todo = 0;
        for (const row of visibleRows) {
            if (row.worktypeId != null) mapped++;
            else if (row.updatedAt) none++;
            else todo++;
        }
        return { mapped, none, todo };
    }, [visibleRows]);

    const isDirty = (row: MatchingRow) => {
        const draft = drafts[rowKey(row)];
        const initial = draftFromRow(row);
        return !!draft && (draft.worktype !== initial.worktype || draft.note !== initial.note);
    };

    const updateDraft = (row: MatchingRow, patch: Partial<Draft>) => {
        setDrafts((prev) => ({ ...prev, [rowKey(row)]: { ...prev[rowKey(row)], ...patch } }));
    };

    const handleSave = async (row: MatchingRow) => {
        const key = rowKey(row);
        const draft = drafts[key];
        if (!draft || draft.worktype === '' || row.subIndustryId == null) return;
        setError('');
        setSuccess('');
        try {
            setSavingKey(key);
            const res = await axios.put(
                `${CONFIG.apiUrl}/admin/batiweb/matching/${row.subIndustryId}`,
                { worktypeId: draft.worktype === 'none' ? null : Number(draft.worktype), note: draft.note },
                { headers }
            );
            const saved: MatchingRow = res.data.mapping;
            setRows((prev) => prev.map((r) => (rowKey(r) === key ? saved : r)));
            setDrafts((prev) => ({ ...prev, [key]: draftFromRow(saved) }));
            setSuccess(
                `${row.subIndustry} → ${saved.worktypeId != null ? `${saved.worktypeId} ${saved.worktypeLabel}` : "pas d'équivalent"}`
            );
        } catch (e: any) {
            setError(e.response?.data?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSavingKey(null);
        }
    };

    // Met à jour les lignes enregistrées en bulk (modale « Dupliquer », onglet « Worktypes à matcher ») et leurs brouillons.
    const applySavedRows = (saved: MatchingRow[]) => {
        const byKey = new Map(saved.map((row) => [rowKey(row), row]));
        setRows((prev) => prev.map((row) => byKey.get(rowKey(row)) ?? row));
        setDrafts((prev) => ({ ...prev, ...Object.fromEntries(saved.map((row) => [rowKey(row), draftFromRow(row)])) }));
    };

    // Retour de la modale « Dupliquer ».
    const handleDuplicated = (saved: MatchingRow[]) => {
        applySavedRows(saved);
        setSuccess(`${duplicateSource?.subIndustry} dupliqué sur ${saved.length} produit(s) : ${saved.map((r) => r.subIndustry).join(', ')}`);
        setError('');
        setDuplicateSource(null);
    };

    const filteredWorktypes = useMemo(() => {
        const q = worktypeSearch.trim().toLowerCase();
        if (!types) return [];
        const list = [...types.worktype].sort((a, b) => a.id - b.id);
        return q ? list.filter((w) => w.label.toLowerCase().includes(q) || String(w.id) === q) : list;
    }, [types, worktypeSearch]);

    return (
        <div className="pt-[50px] flex flex-col gap-6">
            <Card extra="p-5">
                {fromWaterfall && (
                    <button
                        onClick={() => navigate('/back-office/waterfall')}
                        className="mb-3 flex w-fit items-center gap-1 text-sm font-semibold text-gray-500 hover:text-navy-700 dark:hover:text-white"
                    >
                        <MdArrowBack /> Retour au waterfall
                    </button>
                )}
                <div className="mb-2 flex items-center gap-3">
                    <MdConstruction className="text-2xl text-brand-500" />
                    <h2 className="text-2xl font-bold text-navy-700 dark:text-white">Batiweb (HelloArtisan)</h2>
                </div>
                <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
                    Les leads no-match dont la sous-industrie a un worktype sont proposés à HelloArtisan en solution de secours, ainsi que les
                    leads bloqués que les règles de blocage Batiweb acceptent.
                </p>

                <div className="mb-6 flex gap-2 border-b border-gray-200 dark:border-navy-600">
                    {BATIWEB_TABS.map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${
                                activeTab === tab.key
                                    ? 'border-brand-500 text-brand-500'
                                    : 'border-transparent text-gray-500 hover:text-navy-700 dark:hover:text-white'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {activeTab === 'noMatch' && <NoMatchLeadsTab />}
                {activeTab === 'imported' && <ImportedLeadsTab />}
                {activeTab === 'blocked' && <BlockedLeadsTab />}
                {activeTab === 'blockRules' && <BlockRulesTab />}

                {typesError && (activeTab === 'matching' || activeTab === 'todo' || activeTab === 'types') && (
                    <div className="mb-4 flex items-center gap-2 rounded-lg bg-orange-100 p-3 text-sm text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                        <MdWarning className="shrink-0" />
                        <span>
                            GET /types indisponible ({typesError}). Les worktypes ne peuvent pas être modifiés tant que la liste n'est pas
                            récupérée : seuls « Pas d'équivalent » et la note restent éditables.
                        </span>
                    </div>
                )}

                {activeTab === 'matching' && (
                    <>
                        <div className="mb-4 flex flex-wrap items-end gap-4">
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Industrie</label>
                                <select
                                    value={industryFilter}
                                    onChange={(e) => setIndustryFilter(e.target.value)}
                                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                >
                                    <option value="">Toutes les industries</option>
                                    {industries.map((industry) => (
                                        <option key={industry} value={industry}>{industry}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-2 text-xs font-semibold">
                                <span className="rounded-full bg-green-100 px-3 py-1 text-green-700">{counts.mapped} mappée(s)</span>
                                <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-600 dark:bg-navy-700 dark:text-gray-300">{counts.none} sans équivalent</span>
                                <span className="rounded-full bg-orange-100 px-3 py-1 text-orange-700">{counts.todo} à paramétrer</span>
                            </div>
                        </div>

                        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                            Seules les sous-industries avec un worktype sont envoyées. Les noms et le découpage diffèrent entre leadrs et HelloArtisan
                            (ex. Fenêtres → PVC / alu / bois) : un mauvais worktype envoie le lead au mauvais artisan.
                        </p>

                        {error && (
                            <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">{error}</div>
                        )}
                        {success && (
                            <div className="mb-4 rounded-lg bg-green-100 p-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">{success}</div>
                        )}

                        {rowsLoading ? (
                            <div className="py-10 text-center text-gray-400">Chargement...</div>
                        ) : visibleRows.length === 0 ? (
                            <div className="py-10 text-center text-gray-400">Aucune sous-industrie.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 dark:border-navy-600">
                                            {!industryFilter && <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Industrie</th>}
                                            <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Sous-industrie leadrs</th>
                                            <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Worktype HelloArtisan</th>
                                            <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Note</th>
                                            <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Modifié</th>
                                            <th className="pb-3 text-xs font-bold uppercase text-gray-500"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleRows.map((row) => {
                                            const key = rowKey(row);
                                            const draft = drafts[key] ?? draftFromRow(row);
                                            const noId = row.subIndustryId == null;
                                            // Worktype enregistré mais absent de /types : HelloArtisan rejettera les imports.
                                            const stale = !!types && row.worktypeId != null && !worktypeIds.has(row.worktypeId);
                                            const dirty = isDirty(row);
                                            return (
                                                <tr key={key} className="border-b border-gray-100 align-top dark:border-navy-700">
                                                    {!industryFilter && <td className="py-3 pr-4 text-gray-500">{row.industry}</td>}
                                                    <td className="py-3 pr-4 font-semibold text-navy-700 dark:text-white">
                                                        {row.subIndustry}
                                                        {row.subIndustryId != null && (
                                                            <span className="ml-2 text-xs font-normal text-gray-400">#{row.subIndustryId}</span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 pr-4">
                                                        {noId ? (
                                                            <span className="text-xs text-gray-400">Pas de subIndustryId : non paramétrable</span>
                                                        ) : (
                                                            <>
                                                                <select
                                                                    value={draft.worktype}
                                                                    onChange={(e) => updateDraft(row, { worktype: e.target.value })}
                                                                    className="w-72 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                                                >
                                                                    <option value="" disabled>— À paramétrer —</option>
                                                                    <option value="none">Pas d'équivalent (non envoyé)</option>
                                                                    {/* Valeur enregistrée hors liste (ou liste indisponible) : affichée pour ne pas la perdre. */}
                                                                    {row.worktypeId != null && !worktypeIds.has(row.worktypeId) && (
                                                                        <option value={String(row.worktypeId)}>
                                                                            {row.worktypeId} — {row.worktypeLabel ?? '?'}{types ? ' (absent de /types)' : ''}
                                                                        </option>
                                                                    )}
                                                                    {sortedWorktypes.map((w) => (
                                                                        <option key={w.id} value={String(w.id)}>{w.label} ({w.id})</option>
                                                                    ))}
                                                                </select>
                                                                {stale && (
                                                                    <div className="mt-1 flex items-center gap-1 text-xs text-red-600">
                                                                        <MdWarning /> Absent de /types : les imports seront rejetés
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </td>
                                                    <td className="py-3 pr-4">
                                                        <input
                                                            type="text"
                                                            value={draft.note}
                                                            disabled={noId}
                                                            onChange={(e) => updateDraft(row, { note: e.target.value })}
                                                            placeholder="ex. PVC pris par défaut"
                                                            className="w-full min-w-[220px] rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                                        />
                                                    </td>
                                                    <td className="py-3 pr-4 text-xs text-gray-500">
                                                        {row.updatedAt ? (
                                                            <>
                                                                {new Date(row.updatedAt).toLocaleDateString('fr-FR')}
                                                                <div className="text-gray-400">{row.updatedBy}</div>
                                                            </>
                                                        ) : '—'}
                                                    </td>
                                                    <td className="py-3">
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                onClick={() => handleSave(row)}
                                                                disabled={noId || !dirty || draft.worktype === '' || savingKey === key}
                                                                className="flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-40"
                                                            >
                                                                <MdSave />
                                                                {savingKey === key ? 'Enregistrement...' : 'Enregistrer'}
                                                            </button>
                                                            {/* Duplique la correspondance enregistrée (pas le brouillon) : enregistrer d'abord. */}
                                                            <button
                                                                onClick={() => setDuplicateSource(row)}
                                                                disabled={noId || !row.updatedAt || dirty}
                                                                title={dirty ? "Dupliquer — enregistrez d'abord la ligne" : 'Dupliquer : appliquer ce worktype à d\'autres produits leadrs'}
                                                                className="p-1 text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-300 dark:text-navy-500 dark:hover:text-gray-300"
                                                            >
                                                                <MdContentCopy size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'todo' && (
                    <WorktypeTodoTab
                        worktypes={types?.worktype ?? null}
                        typesLoading={typesLoading}
                        rows={rows}
                        rowsLoading={rowsLoading}
                        onMapped={applySavedRows}
                    />
                )}

                {duplicateSource && (
                    <DuplicateMappingModal source={duplicateSource} rows={rows} onClose={() => setDuplicateSource(null)} onApplied={handleDuplicated} />
                )}

                {activeTab === 'types' && (
                    <>
                        <div className="mb-4 flex items-center justify-between gap-4">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Seules ces valeurs sont acceptées à l'import : toute autre valeur fait rejeter le lead (code 3).
                            </p>
                            <button
                                onClick={() => fetchTypes(true)}
                                disabled={typesLoading}
                                className="flex shrink-0 items-center gap-1 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-navy-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-navy-700 dark:text-white"
                            >
                                <MdRefresh />
                                {typesLoading ? 'Chargement...' : 'Rafraîchir'}
                            </button>
                        </div>

                        {!types ? (
                            <div className="py-10 text-center text-gray-400">{typesLoading ? 'Chargement...' : 'Aucune donnée.'}</div>
                        ) : (
                            <>
                                <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                                    {TYPE_LISTS.map((list) => (
                                        <div key={list.key} className="rounded-xl border border-gray-200 p-4 dark:border-navy-600">
                                            <div className="mb-2 text-xs font-bold uppercase text-gray-500">{list.label}</div>
                                            <ul className="text-sm">
                                                {types[list.key].map((option) => (
                                                    <li key={option.id} className="py-0.5 text-navy-700 dark:text-white">
                                                        <span className="mr-2 inline-block w-5 font-mono text-gray-400">{option.id}</span>
                                                        {option.label}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>

                                <div className="mb-3 flex items-center gap-3">
                                    <div className="text-xs font-bold uppercase text-gray-500">Métiers (worktype) — {types.worktype.length}</div>
                                    <input
                                        type="text"
                                        value={worktypeSearch}
                                        onChange={(e) => setWorktypeSearch(e.target.value)}
                                        placeholder="Rechercher un métier ou un id"
                                        className="w-64 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-x-6 text-sm md:grid-cols-2 xl:grid-cols-3">
                                    {filteredWorktypes.map((w) => (
                                        <div key={w.id} className="border-b border-gray-100 py-1 text-navy-700 dark:border-navy-700 dark:text-white">
                                            <span className="mr-2 inline-block w-9 font-mono text-gray-400">{w.id}</span>
                                            {w.label}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                )}
            </Card>
        </div>
    );
}
